import * as React from "react";
import { useCallback, useState } from "react";
import * as styles from "./style.module.css";

const defaultMinRatio = 0.1;
const defaultMaxRatio = 0.9;

interface HorizontalSplitPaneProps {
  /** 比の初期値 */
  defaultRatio?: number;
  /** 比 */
  ratio?: number;
  /** 比の最小値 */
  minRatio?: number;
  /** 比の最大値 */
  maxRatio?: number;
  /** trueを指定するとdividerの幅をそのままを表示します。 未指定だと0pxとして表示します。*/
  showDivider?: boolean;
  /** 子要素(2つ) */
  children: React.ReactNode;
  /** 割合の変更 */
  onChangeRatio?: (ratio: number) => void;
}

/** 水平分割したペインを表示します。
 * @example
 * <HorizontalSplitPane>
 *   <div>left</div>
 *   <div>right</div>
 * </HorizontalSplitPane>
 */
export function HorizontalSplitPane(props: HorizontalSplitPaneProps) {
  const { minRatio, maxRatio, showDivider, onChangeRatio } = props;
  const clampRatio = (ratio: number) => {
    if (ratio < (minRatio ?? defaultMinRatio)) {
      ratio = minRatio ?? defaultMinRatio;
    }
    if (ratio > (maxRatio ?? defaultMaxRatio)) {
      ratio = maxRatio ?? defaultMaxRatio;
    }
    return ratio;
  };
  const [left, right] = React.Children.toArray(props.children);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  const [resizeObserver, setResizeObserver] = useState<
    ResizeObserver | undefined
  >();
  const wrapperRef = useCallback((div: HTMLDivElement | null) => {
    if (div) {
      setWrapperWidth(div.clientWidth);
      const resizeObserver = new ResizeObserver(() => {
        setWrapperWidth(div.clientWidth);
      });
      resizeObserver.observe(div);
      setResizeObserver(resizeObserver);
    } else {
      resizeObserver?.disconnect();
    }
  }, []);
  const [ratio, setRatio] = useState(clampRatio(props.defaultRatio ?? 0.5));
  const actualRatio = props.ratio ?? ratio;

  const onDividerMouseDown = (e: React.MouseEvent) => {
    const orgUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    const startX = e.clientX;
    const startWidth = wrapperWidth * ratio;
    const updateRatio = (x: number) => {
      e.preventDefault();
      if (wrapperWidth > 0) {
        const ratio = clampRatio((startWidth + (x - startX)) / wrapperWidth);
        setRatio(ratio);
        onChangeRatio?.(ratio);
      }
    };
    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      updateRatio(e.clientX);
    };
    const onMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      document.body.style.userSelect = orgUserSelect;
      updateRatio(e.clientX);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const leftSize =
    actualRatio == 0 ? "0px" : `minmax(0, ${actualRatio.toFixed(3)}fr)`;
  const rightSize =
    actualRatio == 1 ? "0px" : `minmax(0, ${(1 - actualRatio).toFixed(3)}fr)`;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `${leftSize} auto ${rightSize}`,
        height: "100%",
        maxHeight: "100%",
        overflow: "auto",
      }}
      ref={wrapperRef}
    >
      {left}
      {actualRatio != 0 && actualRatio != 1 ? (
        <div
          className={styles.horizontalDivider}
          style={{ margin: showDivider ? undefined : "0 -3px" }}
          onMouseDown={onDividerMouseDown}
        />
      ) : (
        <div />
      )}
      {right}
    </div>
  );
}

interface VerticalSplitPaneProps {
  /** 比の初期値 */
  defaultRatio?: number;
  /** 比 */
  ratio?: number;
  /** 比の最小値 */
  minRatio?: number;
  /** 比の最大値 */
  maxRatio?: number;
  /** 子要素(2つ) */
  children: React.ReactNode;
  /** 割合の変更 */
  onChangeRatio?: (ratio: number) => void;
}

/** 垂直分割したペインを表示します。
 * @example
 * <VerticalSplitPane>
 *   <div>top</div>
 *   <div>bottom</div>
 * </VerticalSplitPane>
 */
export function VerticalSplitPane(props: VerticalSplitPaneProps) {
  const { minRatio, maxRatio, onChangeRatio } = props;
  const clampRatio = (ratio: number) => {
    if (ratio < (minRatio ?? defaultMinRatio)) {
      ratio = minRatio ?? defaultMinRatio;
    }
    if (ratio > (maxRatio ?? defaultMaxRatio)) {
      ratio = maxRatio ?? defaultMaxRatio;
    }
    return ratio;
  };
  const [top, bottom] = React.Children.toArray(props.children);
  const [wrapperHeight, setWrapperHeight] = useState(0);
  const [resizeObserver, setResizeObserver] = useState<
    ResizeObserver | undefined
  >();
  const wrapperRef = useCallback((div: HTMLDivElement | null) => {
    if (div) {
      setWrapperHeight(div.clientHeight);
      const resizeObserver = new ResizeObserver(() => {
        setWrapperHeight(div.clientHeight);
      });
      resizeObserver.observe(div);
      setResizeObserver(resizeObserver);
    } else {
      resizeObserver?.disconnect();
    }
  }, []);
  const [ratio, setRatio] = useState(clampRatio(props.defaultRatio ?? 0.5));
  const actualRatio = props.ratio ?? ratio;

  const onDividerMouseDown = (e: React.MouseEvent) => {
    const orgUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    const startY = e.clientY;
    const startHeight = wrapperHeight * ratio;
    const updateRatio = (y: number) => {
      e.preventDefault();
      if (wrapperHeight > 0) {
        const newRatio = clampRatio(
          (startHeight + (y - startY)) / wrapperHeight,
        );
        setRatio(newRatio);
        onChangeRatio?.(newRatio);
      }
    };
    const onMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      updateRatio(e.clientY);
    };
    const onMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      document.body.style.userSelect = orgUserSelect;
      updateRatio(e.clientY);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const topSize =
    actualRatio == 0 ? "0px" : `minmax(0, ${actualRatio.toFixed(3)}fr)`;
  const bottomSize =
    actualRatio == 1 ? "0px" : `minmax(0, ${(1 - actualRatio).toFixed(3)}fr)`;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: `${topSize} auto ${bottomSize}`,
        height: "100%",
        maxHeight: "100%",
        overflow: "auto",
      }}
      ref={wrapperRef}
    >
      {top}
      {actualRatio != 0 && actualRatio != 1 ? (
        <div
          className={styles.verticalDivider}
          onMouseDown={onDividerMouseDown}
        />
      ) : (
        <div />
      )}
      {bottom}
    </div>
  );
}
